import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import * as XLSX from "xlsx";
import Page from "../../../components/Page";
import Scrollbar from "../../../components/Scrollbar";
import AlertMessage from "../../../components/alert/alert";
import orderService from "../services/orderService";

const EMPTY_FILTERS = {
  customer: "",
  startDate: "",
  endDate: "",
  branch: "Todas",
  orderType: "",
  lensId: "",
  makerId: "",
  focusId: "",
  blockId: "",
  photosensitivityId: "",
  designId: "",
  materialId: "",
  customerOrder: "",
  status: "",
  minPairs: "",
  maxPairs: "",
};

const STATUSES = [
  { value: "CADASTRADA", label: "Cadastrada" },
  { value: "1", label: "Baixa" },
  { value: "RECEBIDA", label: "Recebida" },
  { value: "3", label: "Perda" },
  { value: "4", label: "Retrabalho" },
  { value: "6", label: "Reprovada no recebimento" },
  { value: "7", label: "Cancelada" },
];
const EXPORT_LIMIT = 2000;

const statusLabel = (value: string) =>
  STATUSES.find((item) => item.value === String(value))?.label || value || "—";

const toTimestamp = (date: string, end = false) =>
  date ? new Date(`${date}T${end ? "23:59:59" : "00:00:00"}`).getTime() / 1000 : undefined;

const formatDateTime = (timestamp: number) =>
  timestamp
    ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(Number(timestamp) * 1000))
    : "—";

export default function AdvancedOrderFilters() {
  const [filters, setFilters] = useState<any>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<any>(EMPTY_FILTERS);
  const [rows, setRows] = useState<any[]>([]);
  const [lenses, setLenses] = useState<any[]>([]);
  const [options, setOptions] = useState<Record<string, any[]>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [confirmExport, setConfirmExport] = useState(false);
  const requestSequence = useRef(0);

  const requestParams = (source: any, requestedPage = page, limit = rowsPerPage) => ({
    ...source,
    startDate: toTimestamp(source.startDate),
    endDate: toTimestamp(source.endDate, true),
    page: requestedPage + 1,
    limit,
  });

  const loadRows = async (source = appliedFilters, requestedPage = page, limit = rowsPerPage) => {
    const requestId = ++requestSequence.current;
    try {
      setLoading(true);
      const response = await orderService.getAdvanced(requestParams(source, requestedPage, limit));
      if (requestId === requestSequence.current) {
        setRows(response.data?.rows || []);
        setTotal(Number(response.data?.total || 0));
      }
    } catch (requestError) {
      console.error(requestError);
      if (requestId === requestSequence.current) setError("Não foi possível carregar as ordens de serviço.");
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      orderService.getSelect("lens"),
      ...["maker", "focus", "block", "photosensitivity", "design", "material"].map((type) => orderService.getSelect(type)),
    ]).then(([lensResponse, ...responses]) => {
      setLenses(lensResponse.data || []);
      setOptions(Object.fromEntries(["maker", "focus", "block", "photosensitivity", "design", "material"].map((type, index) => [type, responses[index].data || []])));
    }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (name: string, value: any) => setFilters((current: any) => ({ ...current, [name]: value }));

  const apply = () => {
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setError("A data inicial não pode ser posterior à data final.");
      return;
    }
    if (filters.minPairs && filters.maxPairs && Number(filters.minPairs) > Number(filters.maxPairs)) {
      setError("A quantidade mínima de pares não pode ser maior que a máxima.");
      return;
    }
    setError("");
    setAppliedFilters(filters);
    setPage(0);
    setHasSearched(true);
    loadRows(filters, 0, rowsPerPage);
  };

  const clear = () => {
    requestSequence.current += 1;
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
    setRows([]);
    setTotal(0);
    setHasSearched(false);
    setLoading(false);
    setError("");
  };

  const exportRows = async () => {
    try {
      setConfirmExport(false);
      setExporting(true);
      const response = await orderService.getAdvanced({
        ...requestParams(appliedFilters, 0, EXPORT_LIMIT),
        export: true,
      });
      const data = (response.data?.rows || []).map((row: any) => ({
        "Código OS": row.code,
        Cliente: row.customer,
        Sigla: row.customerInitials,
        Unidade: row.branch,
        Tipo: Number(row.orderTypeId) === 2 ? "Cortesia" : "Venda",
        Tratamento: row.lens,
        Fabricante: row.maker,
        "Tipo de lente": row.focus,
        "Observação lente/serviço": row.serviceNote,
        Fotossensibilidade: row.photosensitivity,
        "Inteira/Recortada": row.design,
        Material: row.material,
        "OS do cliente": row.customerOrder,
        Pares: Number(row.pairs || 0),
        Status: statusLabel(row.status),
        Atualização: formatDateTime(row.updatedAt),
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ordens de serviço");
      XLSX.writeFile(workbook, `ordens-servico-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (requestError) {
      console.error(requestError);
      setError("Não foi possível exportar as ordens de serviço.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page title="Filtros avançados de OS">
      <Container maxWidth="xl">
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} mb={3}>
          <Box>
            <Typography variant="h4">Filtros avançados</Typography>
            <Typography color="text.secondary">Consulte, refine e exporte ordens de serviço.</Typography>
          </Box>
          <Box sx={{ textAlign: { sm: "right" } }}>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={exporting || total === 0 || total > EXPORT_LIMIT}
              onClick={() => setConfirmExport(true)}
              sx={{ color: "#004A78", borderColor: "#004A78", "&:hover": { borderColor: "#226C9A", backgroundColor: "rgba(0, 74, 120, 0.04)" } }}
            >
              Exportar resultado
            </Button>
            {hasSearched && total > EXPORT_LIMIT && (
              <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, maxWidth: 360 }}>
                Exportação limitada a 2.000 OS. Refine os filtros ou fale com o administrador para exportar mais dados.
              </Typography>
            )}
          </Box>
        </Stack>

        <Card sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" label="Cliente ou sigla" value={filters.customer} onChange={(e) => updateFilter("customer", e.target.value)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="date" label="Data inicial" InputLabelProps={{ shrink: true }} value={filters.startDate} onChange={(e) => updateFilter("startDate", e.target.value)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="date" label="Data final" InputLabelProps={{ shrink: true }} value={filters.endDate} onChange={(e) => updateFilter("endDate", e.target.value)} /></Grid>
            <Grid item xs={12} sm={6} md={2}><FormControl fullWidth size="small"><InputLabel>Unidade</InputLabel><Select label="Unidade" value={filters.branch} onChange={(e) => updateFilter("branch", e.target.value)}><MenuItem value="Todas">Todas</MenuItem>{["NE", "SP", "PA"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={6} md={2}><FormControl fullWidth size="small"><InputLabel>Tipo</InputLabel><Select label="Tipo" value={filters.orderType} onChange={(e) => updateFilter("orderType", e.target.value)}><MenuItem value="">Todos</MenuItem><MenuItem value="1">Venda</MenuItem><MenuItem value="2">Cortesia</MenuItem></Select></FormControl></Grid>
            <Grid item xs={12} sm={6} md={2}><FormControl fullWidth size="small"><InputLabel>Status</InputLabel><Select label="Status" value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}><MenuItem value="">Todos</MenuItem>{STATUSES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={6} md={4}><FormControl fullWidth size="small"><InputLabel>Tratamento</InputLabel><Select label="Tratamento" value={filters.lensId} onChange={(e) => updateFilter("lensId", e.target.value)}><MenuItem value="">Todos</MenuItem>{lenses.map((item) => <MenuItem key={item.id_Lens} value={item.id_Lens}>{item.name_Lens}</MenuItem>)}</Select></FormControl></Grid>
            {[
              ["makerId", "Fabricante", "maker"],
              ["focusId", "Tipo de lente", "focus"],
              ["blockId", "Observação lente/serviço", "block"],
              ["photosensitivityId", "Fotossensibilidade", "photosensitivity"],
              ["designId", "Inteira/Recortada", "design"],
              ["materialId", "Material", "material"],
            ].map(([field, label, type]) => <Grid item xs={12} sm={6} md={4} key={field}><FormControl fullWidth size="small"><InputLabel>{label}</InputLabel><Select label={label} value={filters[field]} onChange={(e) => updateFilter(field, e.target.value)}><MenuItem value="">Todos</MenuItem>{(options[type] || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</Select></FormControl></Grid>)}
            <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" label="OS do cliente" value={filters.customerOrder} onChange={(e) => updateFilter("customerOrder", e.target.value)} /></Grid>
            <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="number" label="Mín. pares" value={filters.minPairs} onChange={(e) => updateFilter("minPairs", e.target.value)} /></Grid>
            <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="number" label="Máx. pares" value={filters.maxPairs} onChange={(e) => updateFilter("maxPairs", e.target.value)} /></Grid>
            <Grid item xs={12} md={8}><Stack direction="row" spacing={1} justifyContent="flex-end"><Button startIcon={<RestartAltIcon />} onClick={clear} sx={{ color: "#004A78" }}>Limpar</Button><Button variant="contained" startIcon={<SearchIcon />} onClick={apply} disabled={loading} sx={{ backgroundColor: "#004A78", "&:hover": { backgroundColor: "#226C9A" } }}>Aplicar filtros</Button></Stack></Grid>
          </Grid>
        </Card>

        <Card>
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}><Typography variant="subtitle1" fontWeight={700}>{hasSearched ? `${total.toLocaleString("pt-BR")} ordens encontradas` : "Aplique os filtros para consultar as ordens"}</Typography></Box>
          <Scrollbar><TableContainer sx={{ minWidth: 1900 }}><Table size="small"><TableHead><TableRow>{["OS", "OS cliente", "Cliente", "Unidade", "Status", "Tratamento", "Fabricante", "Tipo de lente", "Observação/Serviço", "Fotossensibilidade", "Inteira/Recortada", "Material", "Pares", "Atualização"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead><TableBody>
            {loading ? <TableRow><TableCell colSpan={14} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow> : !hasSearched ? <TableRow><TableCell colSpan={14} align="center" sx={{ py: 6 }}>Use os filtros acima e clique em “Aplicar filtros” para iniciar a consulta.</TableCell></TableRow> : rows.length === 0 ? <TableRow><TableCell colSpan={14} align="center" sx={{ py: 6 }}>Nenhuma OS encontrada para os filtros selecionados.</TableCell></TableRow> : rows.map((row) => <TableRow hover key={`${row.customerId}-${row.orderId}`}><TableCell sx={{ fontWeight: 700 }}>{row.code}</TableCell><TableCell>{row.customerOrder || "—"}</TableCell><TableCell>{row.customer}<Typography variant="caption" display="block" color="text.secondary">{row.customerInitials}</Typography></TableCell><TableCell>{row.branch}</TableCell><TableCell>{statusLabel(row.status)}</TableCell><TableCell>{row.lens}</TableCell><TableCell>{row.maker || "—"}</TableCell><TableCell>{row.focus || "—"}</TableCell><TableCell>{row.serviceNote || "—"}</TableCell><TableCell>{row.photosensitivity || "—"}</TableCell><TableCell>{row.design || "—"}</TableCell><TableCell>{row.material || "—"}</TableCell><TableCell>{Number(row.pairs || 0).toLocaleString("pt-BR")}</TableCell><TableCell>{formatDateTime(row.updatedAt)}</TableCell></TableRow>)}
          </TableBody></Table></TableContainer></Scrollbar>
          <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Linhas por página" onPageChange={(_event, nextPage) => { setPage(nextPage); loadRows(appliedFilters, nextPage, rowsPerPage); }} onRowsPerPageChange={(event) => { const next = Number(event.target.value); setRowsPerPage(next); setPage(0); loadRows(appliedFilters, 0, next); }} />
        </Card>
        <AlertMessage open={Boolean(error)} message={error} />
        <Dialog open={confirmExport} onClose={() => setConfirmExport(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmar exportação</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você está prestes a exportar <strong>{total.toLocaleString("pt-BR")} ordens de serviço</strong> com os filtros aplicados.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              O limite permitido é de {EXPORT_LIMIT.toLocaleString("pt-BR")} OS por arquivo.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmExport(false)} sx={{ color: "#004A78" }}>Cancelar</Button>
            <Button onClick={exportRows} variant="contained" sx={{ backgroundColor: "#004A78", "&:hover": { backgroundColor: "#226C9A" } }}>
              Exportar {total.toLocaleString("pt-BR")} OS
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
